import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  Users, 
  FolderKanban, 
  Activity, 
  Tags,
  TrendingUp,
  Bot,
  RefreshCw,
  Bell,
  ChevronDown
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { toast } from "@/components/toast";
import { NotificationContext } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import safeLocalStorage from "@/utils/safeStorage";
import { TELEGRAM_CATEGORIES } from "@/constants/telegramCategories";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, subtitle, value, color = "#00F0FF", trend }) => (
  <div className="stat-card card-hover" data-testid={`stat-${label.replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-label">{label}</p>
        {subtitle && (
          <p className="text-sm text-cyan-400/80 mt-0.5 font-medium">{subtitle}</p>
        )}
        <p className="stat-value" style={{ color }}>{value}</p>
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <TrendingUp size={14} className="text-green-500" />
            <span className="text-xs text-green-500">{trend}</span>
          </div>
        )}
      </div>
      <div 
        className="w-12 h-12 rounded-lg flex items-center justify-center"
        style={{ background: `${color}20` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_groups: 0,
    total_members: 0,
    active_members: 0,
    total_activities: 0,
    total_categories: 0,
    bot_status: "disconnected"
  });
  const [activityData, setActivityData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [allCategoriesStats, setAllCategoriesStats] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  
  // Translations and language
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Get notification context
  const notifications = useContext(NotificationContext);

  const fetchData = async () => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, activityRes, categoriesRes, usageRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/activity-chart`, { headers }),
        axios.get(`${API}/dashboard/categories-distribution`, { headers }),
        axios.get(`${API}/subscription/usage`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setActivityData(activityRes.data);
      setCategoriesData(categoriesRes.data);
      if (usageRes.data) {
        setSubscriptionUsage(usageRes.data);
      }
      
      // Use categories from API directly (they now include proper IDs)
      const apiCategoriesMap = {};
      categoriesRes.data.forEach(cat => {
        apiCategoriesMap[cat.id] = cat;
      });
      
      // Map all 47 categories with their counts from API
      const allStats = TELEGRAM_CATEGORIES.map(cat => {
        const apiCat = apiCategoriesMap[cat.id];
        return {
          id: cat.id,
          name_en: cat.name_en,
          name_ar: cat.name_ar,
          color: cat.color,
          count: apiCat ? apiCat.count : 0
        };
      });
      
      // Sort by count descending, but keep "Other" at the end
      allStats.sort((a, b) => {
        // "Other" always goes to the end
        if (a.id === 'other' || a.name_en === 'Other') return 1;
        if (b.id === 'other' || b.name_en === 'Other') return -1;
        // Sort by count descending
        return b.count - a.count;
      });
      
      setAllCategoriesStats(allStats);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Test notification function
  const testNotification = async () => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Fetch current stats to get actual member count
      const response = await axios.get(`${API}/dashboard/stats`, { headers });
      const currentMembers = response.data.total_members;
      const newMembers = Math.floor(Math.random() * 10) + 1;
      toast.members(currentMembers, newMembers);
    } catch (error) {
      // Fallback
      toast.members(50, Math.floor(Math.random() * 10) + 1);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide">{t('dashboard.title')}</h1>
          <p className="text-neutral-500 mt-1">{t('dashboard.recentActivity')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            data-testid="test-notification-btn"
            onClick={testNotification}
            className="btn-ghost flex items-center gap-2"
            title={t('notifications.newMember')}
          >
            <Bell size={18} />
            {t('common.testAlert')}
          </button>
          <button 
            data-testid="refresh-btn"
            onClick={fetchData}
            className="btn-ghost flex items-center gap-2"
          >
            <RefreshCw size={18} />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Subscription Usage Bar */}
      {subscriptionUsage && (
        <div 
          data-testid="subscription-usage-bar"
          className={`stat-card p-4 mb-6 ${!subscriptionUsage.can_extract ? 'border-2 border-red-500' : ''}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                <Users size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-400">
                  {isRTL ? 'استخدام خطة' : 'Plan Usage'}: <span className="text-cyan-400 font-bold">{isRTL ? subscriptionUsage.plan_name_ar : subscriptionUsage.plan_name_en}</span>
                </p>
                <p className="text-xs text-neutral-500">
                  {subscriptionUsage.extracted_count.toLocaleString()} / {subscriptionUsage.limit.toLocaleString()} {isRTL ? 'عضو' : 'members'}
                </p>
              </div>
            </div>
            <div className={isRTL ? "text-left" : "text-right"}>
              <p className={`text-2xl font-bold ${subscriptionUsage.usage_percent >= 100 ? 'text-red-400' : subscriptionUsage.usage_percent >= 80 ? 'text-yellow-400' : 'text-green-400'}`}>
                {subscriptionUsage.usage_percent}%
              </p>
              <p className="text-xs text-neutral-500">
                {isRTL ? 'المتبقي' : 'Remaining'}: {subscriptionUsage.remaining.toLocaleString()}
              </p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="h-3 bg-black/30 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(subscriptionUsage.usage_percent, 100)}%`,
                background: subscriptionUsage.usage_percent >= 100 
                  ? '#FF0000' 
                  : subscriptionUsage.usage_percent >= 80 
                    ? 'linear-gradient(to right, #EAB308, #F97316)' 
                    : 'linear-gradient(to right, #06B6D4, #3B82F6)'
              }}
            />
          </div>
          {!subscriptionUsage.can_extract && (
            <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(255, 0, 0, 0.2)', border: '1px solid #FF0000' }}>
              <p className="text-sm font-medium" style={{ color: '#FF0000' }}>
                ⚠️ {isRTL ? subscriptionUsage.message_ar : subscriptionUsage.message_en}
              </p>
              <a 
                href="/subscription" 
                className="inline-block mt-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
              >
                {isRTL ? '🚀 ترقية الخطة الآن' : '🚀 Upgrade Now'}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard 
          icon={FolderKanban} 
          label={t('dashboard.totalGroups')} 
          value={stats.total_groups}
          color="#00F0FF"
        />
        <StatCard 
          icon={Users} 
          label={t('dashboard.totalMembers')} 
          value={stats.total_members}
          color="#00FF94"
        />
        <StatCard 
          icon={Activity} 
          label={t('dashboard.activeMembers')} 
          value={stats.active_members}
          color="#7000FF"
        />
        <StatCard 
          icon={Tags} 
          label={t('dashboard.totalCategories')} 
          subtitle={t('dashboard.totalCategoriesSubtitle')}
          value={stats.total_categories}
          color="#FF9500"
        />
      </div>

      {/* Charts */}
      <div className="bento-grid">
        {/* Activity Chart */}
        <div className="col-span-8 chart-container">
          <h3 className="text-lg font-semibold mb-4">{t('dashboard.weeklyActivity')}</h3>
          <div className="h-[300px]">
            {activityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717A"
                    tick={{ fill: '#71717A', fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#71717A"
                    tick={{ fill: '#71717A', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#0A0A0A', 
                      border: '1px solid #00F0FF',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#FAFAFA' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="activities" 
                    stroke="#00F0FF" 
                    strokeWidth={2}
                    dot={{ fill: '#00F0FF', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#00F0FF' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">
                <Activity className="empty-state-icon mx-auto" />
                <p>{t('common.noData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Categories Distribution */}
        <div className="col-span-4 chart-container">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('dashboard.categoriesDistribution')}</h3>
            <Select value={selectedCategoryFilter} onValueChange={setSelectedCategoryFilter}>
              <SelectTrigger className="w-[160px] bg-purple-600 hover:bg-purple-700 border-purple-500 text-white text-sm">
                <SelectValue placeholder={isRTL ? 'عرض الفئات' : 'View Categories'} />
              </SelectTrigger>
              <SelectContent className="max-h-[250px]">
                <SelectItem value="all">{t('dashboard.allMainCategories')}</SelectItem>
                <SelectItem value="active">{t('dashboard.activeMainCategories')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Categories Stats Summary */}
          <div className="flex items-center justify-between mb-4 p-3 bg-black/30 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">
                {allCategoriesStats.filter(c => c.count > 0).length}
              </p>
              <p className="text-xs text-white/70">{t('dashboard.activeMainCategories')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">
                {allCategoriesStats.reduce((sum, c) => sum + c.count, 0)}
              </p>
              <p className="text-xs text-white/70">{isRTL ? 'إجمالي الأعضاء' : 'Total Members'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">47</p>
              <p className="text-xs text-white/70">{t('dashboard.allMainCategories')}</p>
            </div>
          </div>
          
          {/* Categories List with Progress Bars */}
          <div className="h-[280px] overflow-y-auto custom-scrollbar space-y-2 pr-2" style={{ colorScheme: 'dark' }}>
            {(selectedCategoryFilter === 'active' 
              ? allCategoriesStats.filter(c => c.count > 0)
              : allCategoriesStats
            ).map((cat, idx) => {
              const maxCount = Math.max(...allCategoriesStats.map(c => c.count), 1);
              const percentage = (cat.count / maxCount) * 100;
              
              return (
                <div 
                  key={cat.id} 
                  className="p-2 rounded-lg transition-all hover:bg-white/5"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ 
                          backgroundColor: cat.color, 
                          boxShadow: `0 0 12px ${cat.color}, 0 0 20px ${cat.color}40`,
                          border: '2px solid rgba(255, 255, 255, 0.3)'
                        }}
                      ></div>
                      <span style={{ 
                        color: '#FFFFFF', 
                        fontSize: '0.875rem', 
                        fontWeight: '500',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {isRTL ? cat.name_ar : cat.name_en}
                      </span>
                    </div>
                    <span style={{ 
                      color: '#FFFFFF', 
                      fontSize: '0.875rem', 
                      fontWeight: '700',
                      fontFamily: "'JetBrains Mono', monospace"
                    }}>
                      {cat.count}
                    </span>
                  </div>
                  {/* Progress Bar */}
                  <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: cat.color,
                        boxShadow: cat.count > 0 ? `0 0 10px ${cat.color}` : 'none'
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bot Status Card with Toggle */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
              stats.bot_status === 'connected' ? 'bg-green-500/20' : 'bg-neutral-800'
            }`}>
              <Bot size={32} className={stats.bot_status === 'connected' ? 'text-green-500' : 'text-neutral-500'} />
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {t('dashboard.botStatus')}
              </h3>
              <p className="text-neutral-500 text-sm mt-1">
                {stats.bot_status === 'connected' 
                  ? t('dashboard.botMonitoring')
                  : t('dashboard.addTelegramApiToStart')}
              </p>
            </div>
          </div>
          
          {/* Bot Status Toggle Button */}
          <div className="flex items-center gap-3">
            <div 
              className="relative flex items-center p-1 rounded-full bg-neutral-200 dark:bg-gradient-to-br dark:from-[#1a1a3e] dark:to-[#0f0f2e] border border-neutral-300 dark:border-white/10"
            >
              {/* Disconnected Option */}
              <div 
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-default ${
                  stats.bot_status !== 'connected' 
                    ? 'text-white' 
                    : 'text-neutral-600 dark:text-neutral-500'
                }`}
              >
                {isRTL ? 'غير متصل' : 'Disconnected'}
              </div>
              
              {/* Connected Option */}
              <div 
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-default ${
                  stats.bot_status === 'connected' 
                    ? 'text-white' 
                    : 'text-neutral-600 dark:text-neutral-500'
                }`}
              >
                {isRTL ? 'متصل' : 'Connected'}
              </div>
              
              {/* Sliding Background */}
              <div 
                className="absolute top-1 h-[calc(100%-8px)] rounded-full transition-all duration-300"
                style={{
                  width: 'calc(50% - 4px)',
                  left: stats.bot_status === 'connected' 
                    ? (isRTL ? '4px' : 'calc(50%)') 
                    : (isRTL ? 'calc(50%)' : '4px'),
                  background: stats.bot_status === 'connected' 
                    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  boxShadow: stats.bot_status === 'connected'
                    ? '0 0 15px rgba(16, 185, 129, 0.5)'
                    : '0 0 15px rgba(239, 68, 68, 0.5)'
                }}
              />
            </div>
            
            {/* Status Indicator Dot */}
            <div className={`w-3 h-3 rounded-full ${
              stats.bot_status === 'connected' 
                ? 'bg-green-500 animate-pulse' 
                : 'bg-red-500'
            }`} style={{
              boxShadow: stats.bot_status === 'connected'
                ? '0 0 10px rgba(16, 185, 129, 0.8)'
                : '0 0 10px rgba(239, 68, 68, 0.8)'
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}
